import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

// In development the API runs on :5000 of whatever machine served the page,
// so a phone on the Wi-Fi (http://10.0.0.x:3000) reaches the laptop rather
// than its own localhost.
const SOCKET_URL = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : `${window.location.protocol}//${window.location.hostname}:5000`;

const SocketContext = createContext({ socket: null, connected: false });

// One socket connection for the whole session, opened while `user` is signed
// in and torn down on sign-out — replaces the old per-page connection that
// DirectMessages used to open/close on its own mount/unmount.
export function SocketProvider({ user, children }) {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnected(false);
      return undefined;
    }

    const s = io(SOCKET_URL, { withCredentials: true });
    socketRef.current = s;
    setSocket(s);

    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));

    return () => {
      s.disconnect();
      socketRef.current = null;
    };
  // Keyed on the id: refreshUser() swaps the user object without a new account, and a new object here would drop and reopen the socket.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
